import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import {
  HEDGED_COLOR_HEX,
  UNHEDGED_COLOR_HEX,
  VENUE_WARN_COLOR_HEX
} from './descalceColors';
import { buildDescalceDeepLink } from './descalceDeepLink';
import { hasUnreadLeg, unreadLegTitle } from './descalceLegs';
import { TriageMarks } from '../triage/TriageMarks';
import { triagePairDeepLink } from '../triage/triageDeepLink';
import { hasTriageMarks, type TriageRowCounts } from '../triage/triageMarkText';
import {
  accountPairOrUnknown,
  isAccountPair,
  type OpenerDescalceAccountPair,
  type OpenerDescalcePlatform,
  type OpenerDescalceRow
} from '../../api/useOpenerDescalce';

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const NEUTRAL_COLOR = 'grey.500';

const UNREAD_VALUE_TITLE =
  'No cross-venue descalce for this row — one of its legs was not read, so the position is unknown rather than flat.';

const INCOMPLETE_TITLE =
  'INCOMPLETE MARKET: this rule is missing a ticker or a Polymarket leg, or its two Polymarket tokens collide — in which case the Poly counts are SUPPRESSED, not zero. Either way the number above is not a true cross-venue descalce.';

/**
 * `Descalce` column cell — the sort key. Grey `—` when the row's accounts are
 * unreadable, when a leg went unread, or when the market is claimed by more
 * than one rule (`ambiguous`, an account-wide read that belongs to no single
 * row); red beyond the `IMBALANCE_TOLERANCE`; amber for an incomplete market
 * within tolerance; green otherwise.
 *
 * There is no `resolving` case: a row whose leg already resolved at its venue
 * never reaches this table.
 */
export const DescalceCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.venue) {
    return (
      <Box component="span" data-descalce-state="unavailable" sx={{ color: NEUTRAL_COLOR }}>
        —
      </Box>
    );
  }
  // An unread leg outranks every other caveat: with one side unknown there is
  // no cross-venue difference to state, and a number here would be read as
  // one. Checked against `legsRead` as well as the value, so a payload that
  // contradicts itself still renders the unknown.
  if (hasUnreadLeg(row) || row.venue.descalce == null) {
    return (
      <Tooltip title={unreadLegTitle(row) || UNREAD_VALUE_TITLE} placement="top">
        <Box
          component="span"
          data-descalce-state="unread"
          sx={{ color: NEUTRAL_COLOR, borderBottom: `1px dotted ${NEUTRAL_COLOR}` }}
        >
          —
        </Box>
      </Tooltip>
    );
  }
  if (row.venue.ambiguous) {
    return (
      <Tooltip
        title="More than one rule claims this market — this venue read is account-wide and belongs to no single row."
        placement="top"
      >
        <Box
          component="span"
          data-descalce-state="ambiguous"
          sx={{ color: NEUTRAL_COLOR, borderBottom: `1px dotted ${NEUTRAL_COLOR}` }}
        >
          —
        </Box>
      </Tooltip>
    );
  }
  const value = row.venue.descalce;
  const incomplete = row.venue.marketsIncomplete === true;
  // Magnitude decides. `incomplete` merely blocks GREEN — "hedged" is a claim
  // one-venue counts cannot support — without downgrading a real one-venue
  // naked leg to amber, which reads as "expected transient" in `live/NetCell`.
  const color =
    value > IMBALANCE_TOLERANCE
      ? UNHEDGED_COLOR_HEX
      : incomplete
        ? VENUE_WARN_COLOR_HEX
        : HEDGED_COLOR_HEX;
  const title = incomplete
    ? INCOMPLETE_TITLE
    : 'Leg imbalance in contracts, account-wide for this market.';
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-descalce-state="naked"
        data-descalce-incomplete={incomplete ? 'true' : undefined}
        sx={{
          color,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          borderBottom: incomplete ? `1px dotted ${color}` : undefined
        }}
      >
        {fmt(value)}
      </Box>
    </Tooltip>
  );
};

/** `YES +N` / `NO +N` from the sign of `venue.signedNet`. `—`, never `flat`,
 *  whenever a leg went unread: `flat` is a claim about a position nobody
 *  read. */
export const SideCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (
    !row.venue ||
    row.venue.ambiguous ||
    hasUnreadLeg(row) ||
    row.venue.signedNet == null
  ) {
    return (
      <Typography component="span" sx={{ fontSize: '0.75rem', color: NEUTRAL_COLOR }}>
        —
      </Typography>
    );
  }
  const net = row.venue.signedNet;
  if (net === 0) {
    return (
      <Typography component="span" sx={{ fontSize: '0.75rem', color: NEUTRAL_COLOR }}>
        flat
      </Typography>
    );
  }
  const label = net > 0 ? `YES +${fmt(net)}` : `NO +${fmt(Math.abs(net))}`;
  return (
    <Typography noWrap title={label} sx={{ fontSize: '0.75rem' }}>
      {label}
    </Typography>
  );
};

/**
 * Chip label/color for a row's `group.podsDesired`. `panicked` is kept
 * distinct from a benign `off` — mirrors the three-way mapping in
 * `entry/groupPodStatus.ts` (`GROUP_POD_STATUS_LABELS` /
 * `GROUP_POD_STATUS_CHIP_COLORS`), since a hard-panicked group is
 * plausibly the very cause of the naked leg this screen exists to surface
 * and must not read the same as a quietly-paused one.
 */
const GROUP_PODS_CHIP: Record<'on' | 'off' | 'panicked', { label: string; color: 'success' | 'default' | 'error' }> = {
  on: { label: 'ON', color: 'success' },
  off: { label: 'OFF', color: 'default' },
  panicked: { label: 'PANICKED', color: 'error' }
};

const ACCOUNT_CHIP_PREFIX: Record<OpenerDescalcePlatform, string> = {
  kalshi: 'K',
  polymarket: 'P'
};

const ACCOUNT_VENUE_LABEL: Record<OpenerDescalcePlatform, string> = {
  kalshi: 'Kalshi',
  polymarket: 'Polymarket'
};

/**
 * One venue's account, ALWAYS rendered — the convention `BucketPage` sets. A
 * chip that appeared only for the unusual account would train the operator to
 * read its ABSENCE as "the main account", and on this screen that misreading
 * turns two accounts' positions into one.
 */
const AccountPairChip = ({
  platform,
  accountId,
  fromGroup
}: {
  platform: OpenerDescalcePlatform;
  accountId: string;
  /** `false` when the group names no pair and these are the accounts the
   *  sweeper actually read the row under. */
  fromGroup: boolean;
}) => (
  <Tooltip
    title={
      fromGroup
        ? `${ACCOUNT_VENUE_LABEL[platform]} account: ${accountId}`
        : `${ACCOUNT_VENUE_LABEL[platform]} account: ${accountId} — the group names no account pair (ungrouped, or only one venue is configured); this is the account the row was READ under.`
    }
  >
    <Chip
      label={`${ACCOUNT_CHIP_PREFIX[platform]}: ${accountId}`}
      size="small"
      color={fromGroup ? 'default' : 'warning'}
      variant="outlined"
      data-row-account={`${platform}:${accountId}`}
      data-row-account-from={fromGroup ? 'group' : 'read'}
      sx={{ fontSize: '0.55rem', height: 16, maxWidth: 110, flexShrink: 0 }}
    />
  </Tooltip>
);

/** Group name + `ON`/`OFF`/`PANICKED` pod chip + the group's venue-account
 *  pair. A stopped group's naked leg must still be listed — this cell only
 *  marks it, never hides it. */
export const GroupCell = ({
  group,
  accounts
}: {
  group: OpenerDescalceRow['group'];
  /** The pair the row was read under — shown when the group names none. */
  accounts: OpenerDescalceAccountPair;
}) => {
  const podsChip = GROUP_PODS_CHIP[group.podsDesired ?? 'off'] ?? GROUP_PODS_CHIP.off;
  // `fromGroup` follows the SHAPE, not mere presence: a group carrying an
  // unusable `accounts` value names no pair, and claiming it does would label
  // the placeholder as the group's own choice.
  const fromGroup = isAccountPair(group.accounts);
  const pair = accountPairOrUnknown(fromGroup ? group.accounts : accounts);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography
        noWrap
        title={group.name ?? 'Ungrouped'}
        sx={{ fontSize: '0.78rem', minWidth: 0 }}
      >
        {group.name ?? 'Ungrouped'}
      </Typography>
      {group.id && (
        <Chip
          label={podsChip.label}
          size="small"
          color={podsChip.color}
          data-group-pods={group.podsDesired ?? 'unknown'}
          sx={{ fontSize: '0.6rem', height: 16, flexShrink: 0 }}
        />
      )}
      <AccountPairChip
        platform="kalshi"
        accountId={pair.kalshiAccountId}
        fromGroup={fromGroup}
      />
      <AccountPairChip
        platform="polymarket"
        accountId={pair.polymarketAccountId}
        fromGroup={fromGroup}
      />
    </Stack>
  );
};

/** Poly / Kalshi per-side contract count cell — plain number, or `—` for a
 *  side that was never read. `0` here asserts the account holds nothing; only
 *  a read may make that assertion. */
export const VenueCountCell = ({ value }: { value: number | null | undefined }) => (
  <Box
    component="span"
    data-venue-count={value == null ? 'unread' : 'read'}
    sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem' }}
  >
    {value == null ? '—' : fmt(value)}
  </Box>
);

export const BeliefCell = ({ row }: { row: OpenerDescalceRow }) => {
  if (!row.belief) {
    return (
      <Tooltip title="No BeliefSnapshot for this rule yet (engine off / restarted).">
        <Box component="span" sx={{ color: NEUTRAL_COLOR }}>—</Box>
      </Tooltip>
    );
  }
  return (
    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.78rem' }}>
      {fmt(row.belief.descalce)}
    </Box>
  );
};

/** `descalce − belief.descalce`, marked when `|diff| > IMBALANCE_TOLERANCE`. */
export const DiffCell = ({ row }: { row: OpenerDescalceRow }) => {
  // A divergence is venue-read minus belief; with a leg unread there is no
  // venue read to subtract, so any number here would be belief against itself.
  if (!row.divergence || hasUnreadLeg(row)) {
    return <Box component="span" sx={{ color: NEUTRAL_COLOR }}>—</Box>;
  }
  const diff = row.divergence.total;
  const flagged = Math.abs(diff) > IMBALANCE_TOLERANCE;
  const title = flagged
    ? 'The engine belief disagrees with the venue read — either a fill the engine never booked, or, on a row whose resolved leg was capped, the naked size that went into settlement.'
    : 'Venue descalce and engine belief agree within tolerance.';
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="span"
        data-diff-flagged={flagged}
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: flagged ? 700 : 400,
          color: flagged ? UNHEDGED_COLOR_HEX : 'inherit'
        }}
      >
        {diff > 0 ? '+' : ''}
        {fmt(diff)}
      </Box>
    </Tooltip>
  );
};

/** Rule status chip + `hedgeCheckEnabled` shield. */
export const RuleCell = ({ row }: { row: OpenerDescalceRow }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
    <Chip
      label={row.ruleStatus}
      size="small"
      title={row.ruleStatus}
      color={row.ruleStatus === 'active' ? 'success' : 'default'}
      sx={{
        fontSize: '0.65rem',
        height: 18,
        textTransform: 'capitalize',
        minWidth: 0,
        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
      }}
    />
    {row.hedgeCheckEnabled && (
      <Tooltip
        title={`hedge-watchdog enabled${
          row.maxUnhedgedContracts != null
            ? ` — max ${row.maxUnhedgedContracts} unhedged contracts`
            : ''
        }`}
      >
        <ShieldOutlinedIcon
          sx={{ fontSize: '0.9rem', color: HEDGED_COLOR_HEX, flexShrink: 0 }}
        />
      </Tooltip>
    )}
  </Stack>
);

/**
 * Red only where the row is unhedged RIGHT NOW by the same predicate the
 * `Descalce` cell colours by — an unreadable or ambiguous account never earns
 * red.
 */
function triageColorHint(row: OpenerDescalceRow): 'red' | 'amber' {
  const venue = row.venue;
  if (!venue || venue.ambiguous) return 'amber';
  // `descalce` is `null` when a leg went unread — "we do not know", not "0".
  // Red is an assertion that the row IS unhedged right now, and an unknown
  // cannot make it, exactly like the unreadable cases above.
  if (venue.descalce == null) return 'amber';
  return venue.descalce > IMBALANCE_TOLERANCE ? 'red' : 'amber';
}

/**
 * `⚠` column — unread stuck-counter cards (keyed on the RULE) and orphaned
 * automation fills (keyed on the PAIR).
 *
 * The chip is NOT gated on the row being descalzada now: a card still waiting
 * for a human on a row that currently reads hedged is exactly the one that
 * must not be hidden. The descalce state only picks the colour.
 */
export const TriageCell = ({
  row,
  counts,
  stale
}: {
  row: OpenerDescalceRow;
  /** `undefined` ⇒ the index is unreadable ⇒ nothing renders here. */
  counts: TriageRowCounts | undefined;
  /** Index older than 3× its poll: the mark was true then, not now. */
  stale?: boolean;
}) => {
  const marks = (
    <TriageMarks
      counts={counts}
      variant="descalce"
      unattributedNote={(counts?.orphan ?? 0) > 0}
      colorHint={triageColorHint(row)}
      deepLinkTo={triagePairDeepLink(row.pairId)}
    />
  );
  if (!stale || !hasTriageMarks(counts)) return marks;
  return (
    <Box
      component="span"
      data-triage-stale="true"
      sx={{ filter: 'grayscale(1)', opacity: 0.6 }}
    >
      {marks}
    </Box>
  );
};

/** `Go` deep-link to `/predictions/opening?group={id}&subgroup={sid}`,
 *  opened in a new tab. Disabled with a tooltip when the row is ungrouped. */
export const GoCell = ({ row }: { row: OpenerDescalceRow }) => {
  const href = buildDescalceDeepLink(row);
  if (!href) {
    return (
      <Tooltip title="Ungrouped rule (core engine) — no group page to open.">
        <span>
          <IconButton size="small" disabled data-go-disabled="true">
            <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Open this group / subgroup in the opener, in a new tab.">
      <IconButton
        size="small"
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-go-href={href}
      >
        <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
      </IconButton>
    </Tooltip>
  );
};
